import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Accordion, Table, Modal } from "react-bootstrap";
import {
  FaArrowLeft, FaBook, FaCheckCircle, FaTimesCircle, FaClock,
  FaCalendarAlt, FaPlayCircle, FaQuestionCircle, FaUserGraduate,
  FaImage, FaMusic, FaVideo, FaEye, FaUser, FaSchool,
  FaGraduationCap, FaUserTie, FaEnvelope, FaPhone, FaIdCard
} from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { ToastContainer, toast } from "react-toastify";
import { formateDatemdy, capitalizeFirstLetterOfEveryWord } from "../../utils/Helper";

const TEAL  = "#006a60";
const NAVY  = "#000666";
const FAIL  = "#ba1a1a";

const CourseStudentPerformance = () => {
  const navigate = useNavigate();
  const { studentId, courseId } = useParams();
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);

  const [loading, setLoading]               = useState(false);
  const [courseData, setCourseData]         = useState(null);
  const [studentProgress, setStudentProgress] = useState(null);
  const [topics, setTopics]                 = useState([]);
  const [showQuizModal, setShowQuizModal]   = useState(false);
  const [selectedQuiz, setSelectedQuiz]     = useState(null);

  const headers = { "auth-token": authToken, "authorization": accessToken };
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);

  useEffect(() => {
    if (studentId && courseId) fetchCourseAttemptDetails();
  }, [studentId, courseId]); // eslint-disable-line

  const fetchCourseAttemptDetails = async () => {
    setLoading(true);
    try {
      const response = await APIServices.post(
        "/admin/student/attempt-course-details",
        { student_id: parseInt(studentId), course_id: parseInt(courseId) },
        headers
      );
      if (response?.data?.status === true) {
        setCourseData(response?.data?.data?.course);
        setStudentProgress(response?.data?.data?.student_progress);
        setTopics(response?.data?.data?.topics || []);
      } else if (response?.status === 401) {
        logout(); navigate("/");
      } else {
        toast(response?.data?.message || "Error fetching course details", { type: "error", position: "top-right", autoClose: 2000 });
      }
    } catch {
      toast("Error fetching course details", { type: "error", position: "top-right", autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  const getResultBadge = (status) => {
    if (status === "Completed" || status === "Pass")
      return <span className="perf-badge success"><FaCheckCircle size={10} /> {status}</span>;
    if (status === "Failed" || status === "Fail")
      return <span className="perf-badge danger"><FaTimesCircle size={10} /> {status}</span>;
    return <span className="perf-badge warn"><FaClock size={10} /> Pending</span>;
  };

  const getLearningIcon = (status) => {
    if (status === "Completed") return <FaCheckCircle style={{ color: TEAL }} />;
    if (status === "Failed")    return <FaTimesCircle style={{ color: FAIL }} />;
    return <FaClock style={{ color: "#d97706" }} />;
  };

  /* ─── Loading ─── */
  if (loading) return (
    <div className="perf-loading">
      <div className="perf-spinner" />
      <p style={{ fontSize: 14, color: "var(--outline)" }}>Loading course performance…</p>
    </div>
  );

  if (!courseData || !studentProgress) return (
    <div className="perf-loading">
      <p style={{ color: "var(--outline)" }}>No data found</p>
      <button className="red_button" onClick={() => navigate(-1)}>Go Back</button>
    </div>
  );

  return (
    <>
      <div className="perf-page">

        {/* ── Header ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="font_blue_18 font-600 mb-0">Student Course Performance</h1>
          <button className="blue_border d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
            <FaArrowLeft size={13} /> Back
          </button>
        </div>

        {/* ── Student Info ── */}
        <div className="perf-card mb-4">
          <div className="perf-card-header navy">
            <FaUserGraduate /> Student Information
          </div>
          <div className="perf-card-body">
            <div className="perf-info-grid">
              <div className="perf-info-item">
                <div className="perf-info-icon navy"><FaUser /></div>
                <div><p className="perf-info-label">Student Name</p><p className="perf-info-value">{studentProgress.student_name || "N/A"}</p></div>
              </div>
              <div className="perf-info-item">
                <div className="perf-info-icon teal"><FaIdCard /></div>
                <div><p className="perf-info-label">Roll Number</p><p className="perf-info-value">{studentProgress.roll_number || "N/A"}</p></div>
              </div>
              <div className="perf-info-item">
                <div className="perf-info-icon coral"><FaGraduationCap /></div>
                <div><p className="perf-info-label">Grade</p><p className="perf-info-value">{studentProgress.grade || "N/A"}</p></div>
              </div>
              <div className="perf-info-item">
                <div className="perf-info-icon amber"><FaUserTie /></div>
                <div><p className="perf-info-label">Father's Name</p><p className="perf-info-value">{studentProgress.father_name || "N/A"}</p></div>
              </div>
              <div className="perf-info-item">
                <div className="perf-info-icon navy"><FaSchool /></div>
                <div><p className="perf-info-label">School</p><p className="perf-info-value">{studentProgress.school_name || "N/A"}</p></div>
              </div>
              <div className="perf-info-item">
                <div className="perf-info-icon teal"><FaEnvelope /></div>
                <div><p className="perf-info-label">Email</p><p className="perf-info-value">{studentProgress.email || "N/A"}</p></div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Course Info ── */}
        <div className="perf-card mb-4">
          <div className="perf-card-header teal">
            <FaBook /> Course Information
          </div>
          <div className="perf-card-body">
            <div className="d-flex gap-4 flex-wrap">
              {courseData.image ? (
                <img src={courseData.image} alt={courseData.title}
                  style={{ width: 140, height: 110, objectFit: "cover", borderRadius: "var(--radius-md)", flexShrink: 0 }} />
              ) : (
                <div style={{ width: 140, height: 110, background: "var(--surface-low)", borderRadius: "var(--radius-md)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <FaBook size={36} style={{ color: "var(--outline)" }} />
                </div>
              )}
              <div>
                <h2 className="font_blue_18 font-600 mb-1">{courseData.title}</h2>
                <p className="font_grey_12 mb-3">{courseData.sub_title}</p>
                <div className="d-flex flex-wrap gap-2">
                  <span className="perf-badge info"><FaBook size={10} /> {courseData.unique_course_id}</span>
                  <span className="perf-badge neutral"><FaClock size={10} /> {courseData.duration}</span>
                  <span className="perf-badge success"><FaCalendarAlt size={10} /> Expires: {formateDatemdy(courseData.expiry_date)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Access Info ── */}
        <div className="perf-card mb-4">
          <div className="perf-card-header surface">
            <MdTrendingUp style={{ color: NAVY }} /> Access Timeline
          </div>
          <div className="perf-card-body">
            <div className="d-flex gap-4 flex-wrap">
              <div className="perf-info-item">
                <div className="perf-info-icon navy"><FaClock /></div>
                <div>
                  <p className="perf-info-label">First Accessed</p>
                  <p className="perf-info-value">{studentProgress.first_accessed ? formateDatemdy(studentProgress.first_accessed) : "N/A"}</p>
                </div>
              </div>
              <div className="perf-info-item">
                <div className="perf-info-icon teal"><FaClock /></div>
                <div>
                  <p className="perf-info-label">Last Accessed</p>
                  <p className="perf-info-value">{studentProgress.last_accessed ? formateDatemdy(studentProgress.last_accessed) : "N/A"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Topics Accordion ── */}
        <p className="perf-section-title"><FaBook /> Course Content &amp; Progress</p>
        <Accordion defaultActiveKey="0" className="perf-accordion">
          {topics.map((topic, i) => (
            <Accordion.Item eventKey={String(i)} key={topic.id}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                  <div>
                    <strong>{topic.sr_no} — {topic.name}</strong>
                    {topic.text && <p className="font_grey_12 mb-0 mt-1">{topic.text}</p>}
                  </div>
                  <span className="perf-badge neutral" style={{ fontSize: 11 }}>{topic.no_of_lesson} Lessons</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="todoList_table">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Lesson Name</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Result</th>
                        <th>Attempts</th>
                        <th>Media Views</th>
                        <th>First Accessed</th>
                        <th>Last Accessed</th>
                        <th>Quiz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topic.lessons?.map((lesson) => (
                        <tr key={lesson.id}>
                          <td>{lesson.sr_no}</td>
                          <td>
                            <strong>{lesson.name}</strong>
                            {lesson.lesson_text && <div className="font_grey_12">{lesson.lesson_text}</div>}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {getLearningIcon(lesson.learning_status)}
                              <span style={{ fontSize: 13 }}>{lesson.learning_status || "Pending"}</span>
                            </div>
                          </td>
                          <td>{lesson.score}%</td>
                          <td>{getResultBadge(lesson.result_status)}</td>
                          <td>{lesson.no_of_attempt || 0}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              {lesson.view_image > 0 && <span className="perf-badge info"><FaImage size={9} /> {lesson.view_image}</span>}
                              {lesson.view_audio > 0 && <span className="perf-badge success"><FaMusic size={9} /> {lesson.view_audio}</span>}
                              {lesson.view_video > 0 && <span className="perf-badge warn"><FaVideo size={9} /> {lesson.view_video}</span>}
                              {!lesson.view_image && !lesson.view_audio && !lesson.view_video &&
                                <span className="font_grey_12">No views</span>}
                            </div>
                          </td>
                          <td style={{ fontSize: 13 }}>{lesson.first_accessed ? formateDatemdy(lesson.first_accessed) : "—"}</td>
                          <td style={{ fontSize: 13 }}>{lesson.last_accessed ? formateDatemdy(lesson.last_accessed) : "—"}</td>
                          <td>
                            {lesson.quiz_data?.length > 0 && (
                              <button className="blue_border d-flex align-items-center gap-1"
                                style={{ padding: "4px 10px", fontSize: 12 }}
                                onClick={() => { setSelectedQuiz({ data: lesson.quiz_data, noOfQuestion: lesson.no_of_question }); setShowQuizModal(true); }}>
                                <FaEye size={11} /> View ({lesson.quiz_data.length})
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>

      {/* ── Quiz Modal ── */}
      <Modal show={showQuizModal} onHide={() => { setShowQuizModal(false); setSelectedQuiz(null); }} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: "1px solid var(--outline-variant)" }}>
          <Modal.Title className="font_blue_18 font-600">Quiz Questions &amp; Answers</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 500, overflowY: "auto", padding: 20 }}>
          {selectedQuiz && (() => {
            const quizData = selectedQuiz.data || [];
            const perAttempt = selectedQuiz.noOfQuestion || 5;
            const attempts = [];
            for (let i = 0; i < quizData.length; i += perAttempt) attempts.push(quizData.slice(i, i + perAttempt));
            const total = attempts.length;
            return attempts.map((attempt, aIdx) => (
              <div key={aIdx} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3 p-2"
                  style={{ background: "var(--surface-low)", borderRadius: "var(--radius-md)" }}>
                  <h4 className="font_blue_16 font-600 mb-0">Attempt {total - aIdx}</h4>
                  <div className="d-flex gap-2">
                    <span className="perf-badge neutral">{attempt.length} Questions</span>
                    <span className={`perf-badge ${attempt.some(q => q.is_correct) ? "success" : "danger"}`}>
                      Score: {attempt.filter(q => q.is_correct).length}/{attempt.length}
                    </span>
                  </div>
                </div>
                {attempt.map((quiz, qIdx) => (
                  <div key={`${quiz.quiz_id}_${aIdx}_${qIdx}`} className="perf-card mb-3">
                    <div className="perf-card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="font_blue_14 font-600">Question {qIdx + 1}</span>
                        {quiz.is_correct
                          ? <span className="perf-badge success"><FaCheckCircle size={10} /> Correct</span>
                          : <span className="perf-badge danger"><FaTimesCircle size={10} /> Incorrect</span>}
                      </div>
                      <p className="font_blue_14" style={{ marginBottom: 10 }}>{quiz.question}</p>
                      <div className="mb-2">
                        <strong className="font_grey_12">Student Answer:</strong>
                        <div style={{ padding: "8px 12px", marginTop: 4, borderRadius: "var(--radius-sm)", background: quiz.is_correct ? "rgba(0,106,96,0.08)" : "rgba(186,26,26,0.06)", fontSize: 13 }}>
                          {quiz.student_answer || "No answer"}
                        </div>
                      </div>
                      {!quiz.is_correct && quiz.correct_answer && (
                        <div>
                          <strong className="font_grey_12">Correct Answer:</strong>
                          <div style={{ padding: "8px 12px", marginTop: 4, borderRadius: "var(--radius-sm)", background: "rgba(0,106,96,0.08)", fontSize: 13 }}>
                            {quiz.correct_answer}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid var(--outline-variant)" }}>
          <button className="blue_border" onClick={() => { setShowQuizModal(false); setSelectedQuiz(null); }}>Close</button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </>
  );
};

export default CourseStudentPerformance;
